import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, X, Receipt, Calendar, Mail, Briefcase, Check, Trash2 } from "lucide-react";
import api from "../lib/apiClient";

const TYPE_ICON = {
  overdue: Receipt, opened: Mail, upcoming: Calendar,
  job_assigned: Briefcase, job_unassigned: Briefcase, test: Bell,
  omw_notification_sent: Mail,
};
const TYPE_COLOR = {
  overdue: "text-red-600", opened: "text-purple-600", upcoming: "text-[#0F4C81]",
  job_assigned: "text-emerald-600", job_unassigned: "text-amber-600",
  test: "text-slate-500",
};

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);   // /dashboard/v3 date-based
  const [stored, setStored] = useState([]); // /notifications persistent
  const [unread, setUnread] = useState(0);
  const ref = useRef();

  const load = async () => {
    try {
      const [{ data: dash }, { data: notes }] = await Promise.all([
        api.get("/dashboard/v3").catch(() => ({ data: {} })),
        api.get("/notifications?limit=30").catch(() => ({ data: [] })),
      ]);
      setItems(dash.notifications || []);
      setStored(notes || []);
      setUnread((notes || []).filter((n) => !n.read).length);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    load();
  };
  const markAllRead = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };
  const dismiss = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    await api.delete(`/notifications/${id}`).catch(() => {});
    load();
  };

  const badgeCount = unread + items.length;

  return (
    <div ref={ref} className="relative">
      <button data-testid="notifications-bell" onClick={() => setOpen(!open)} className="relative p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md">
        <Bell size={18} className="text-slate-600 dark:text-slate-300" />
        {badgeCount > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {badgeCount > 9 ? "9+" : badgeCount}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md shadow-lg z-50">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
            <h3 className="font-semibold text-sm">Notifications {badgeCount > 0 && <span className="text-xs text-slate-500">({badgeCount})</span>}</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button data-testid="mark-all-read" onClick={markAllRead} className="text-[11px] text-[#0F4C81] hover:underline flex items-center gap-1"><Check size={11}/> Mark all read</button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
            </div>
          </div>
          <div className="max-h-96 overflow-auto">
            {badgeCount === 0 && <div className="px-4 py-8 text-sm text-slate-500 text-center">All caught up ✨</div>}

            {/* Persistent stored notifications first (unread on top) */}
            {[...stored].sort((a, b) => (a.read === b.read ? 0 : a.read ? 1 : -1)).map((n) => {
              const I = TYPE_ICON[n.kind] || Bell;
              return (
                <Link
                  key={n.id}
                  to={n.link || "#"}
                  data-testid={`notif-${n.kind}-${n.id}`}
                  onClick={() => { setOpen(false); if (!n.read) markRead(n.id); }}
                  className={`block px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800 group ${!n.read ? "bg-blue-50/40" : ""}`}
                >
                  <div className="flex items-start gap-2.5">
                    {!n.read && <span className="w-2 h-2 bg-[#0F4C81] rounded-full mt-1.5 shrink-0"/>}
                    {n.read && <span className="w-2 h-2 shrink-0"/>}
                    <I size={14} className={`${TYPE_COLOR[n.kind] || "text-slate-500"} mt-0.5 shrink-0`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{n.title}</div>
                      {n.body && <div className="text-xs text-slate-500 truncate">{n.body}</div>}
                      <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString("en-CA", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" })}</div>
                    </div>
                    <button onClick={(e) => dismiss(e, n.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600 shrink-0" data-testid={`dismiss-notif-${n.id}`}>
                      <Trash2 size={11}/>
                    </button>
                  </div>
                </Link>
              );
            })}

            {/* Date-based dashboard alerts (overdue invoices, opens, upcoming jobs) */}
            {items.map((n, i) => {
              const I = TYPE_ICON[n.type] || Bell;
              return (
                <Link key={`dash-${i}`} to={n.link || "#"} onClick={() => setOpen(false)} className="block px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 shrink-0"/>
                    <I size={14} className={`${TYPE_COLOR[n.type] || ""} mt-0.5`}/>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{n.title}</div>
                      <div className="text-xs text-slate-500">{n.detail}</div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
